import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  CheckCircle2, 
  Mail, 
  Shield, 
  Lightbulb, 
  BarChart3, 
  BookOpen,
  Users,
  Building,
  Heart,
  Briefcase
} from "lucide-react";
import { useState, useEffect } from "react";
import { SEOHead } from "@/components/seo/SEOHead";

const About = () => {
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });

  // Track mouse position for spotlight effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX,
        y: e.clientY
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const values = [
    {
      title: "Growth-Driven Marketing",
      description: "We focus exclusively on marketing activities that drive measurable growth. Our frameworks are built on strategies that have generated millions in revenue for companies across industries."
    },
    {
      title: "Data-Backed Decisions",
      description: "Every recommendation we make is backed by real performance data. We've tested thousands of campaigns and know precisely what drives conversion and customer acquisition at scale."
    },
    {
      title: "Scalable Frameworks",
      description: "We don't believe in quick fixes or marketing 'hacks'. Our approach centers on building sustainable, repeatable marketing systems that continue to deliver results as your company grows."
    },
    {
      title: "Execution Excellence",
      description: "Strategy is only as good as its execution. We provide detailed, actionable guidance on implementation, drawn from our experience scaling marketing operations across multiple successful companies."
    }
  ];

  return (
    <div className="min-h-screen bg-background overflow-hidden">
      <SEOHead
        title="About — LaunchGuide.io"
        description="Created by marketing directors with 20 years of combined experience launching and scaling companies to millions in revenue."
        pathname="/about"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 lg:py-24 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 -z-10">
          {/* Radial gradient background */}
          <div className="absolute inset-0" style={{
            background: "radial-gradient(circle at 50% 30%, rgba(79, 70, 229, 0.15) 0%, rgba(59, 130, 246, 0.1) 25%, rgba(255, 255, 255, 0) 50%)"
          }}></div>
          
          {/* Animated spotlight following mouse */}
          <motion.div 
            className="absolute w-[600px] h-[600px] rounded-full opacity-20 pointer-events-none"
            style={{
              background: "radial-gradient(circle at center, rgba(79, 70, 229, 0.3) 0%, transparent 70%)",
              left: mousePosition.x - 300,
              top: mousePosition.y - 300,
            }}
            animate={{
              left: mousePosition.x - 300,
              top: mousePosition.y - 300,
            }}
            transition={{
              type: "spring",
              damping: 15,
              stiffness: 150,
            }}
          />
          
          {/* Subtle animated particles */}
          <div className="absolute inset-0">
            {Array.from({ length: 20 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute rounded-full bg-blue-400/30 dark:bg-blue-400/40"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: `${Math.random() * 6 + 3}px`,
                  height: `${Math.random() * 6 + 3}px`,
                }}
                animate={{
                  y: [0, Math.random() * 80 - 40],
                  opacity: [0.2, 0.8, 0.2],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: Math.random() * 10 + 10,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: Math.random() * 5,
                }}
              />
            ))}
          </div>
          
          {/* Enhanced floating elements */}
          <motion.div 
            className="absolute top-[15%] right-[10%] w-64 h-64 rounded-full bg-blue-400/10 dark:bg-blue-400/15 blur-3xl" 
            animate={{
              y: [0, 20, 0],
              scale: [1, 1.05, 1],
              opacity: [0.3, 0.5, 0.3]
            }} 
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut"
            }} 
          />
          
          <motion.div 
            className="absolute bottom-[10%] left-[5%] w-72 h-72 rounded-full bg-blue-400/10 dark:bg-blue-400/15 blur-3xl" 
            animate={{
              y: [0, -30, 0],
              scale: [1, 1.1, 1],
              opacity: [0.3, 0.5, 0.3]
            }} 
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }} 
          />
        </div>
        
        <div className="container px-4 md:px-6 relative">
          <div className="max-w-5xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <motion.div 
                className="inline-block rounded-full bg-blue-100 dark:bg-blue-900/30 px-3 py-1 text-sm text-blue-700 dark:text-blue-300 mb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                About Us
              </motion.div>
              
              <motion.h1 
                className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                We've Scaled Companies to <span className="gradient-text">Millions in Revenue</span>
              </motion.h1>
              
              <motion.p 
                className="text-xl text-muted-foreground max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Created by two marketing directors with 20 years of combined experience who have launched and scaled multiple companies to millions of dollars. We've been in the trenches and know what it takes to succeed in today's market.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Our Story Section */}
      <section className="py-20 px-4 md:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-3xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  LaunchGuide was founded by a collective of marketing directors who've led growth at some of the fastest-scaling companies in the tech space. With combined experience of over 20 years in digital marketing, our team has helped launch products that have generated over $100 million in revenue.
                </p>
                <p>
                  After spending decades in the marketing trenches, we saw a clear pattern: brilliant products often fail not because of their quality, but because of ineffective go-to-market strategies. We noticed that even well-funded companies struggled to translate product innovation into market success.
                </p>
                <p>
                  Having personally scaled multiple startups from zero to multi-million dollar businesses, we decided to create LaunchGuide, a platform that distills our battle-tested marketing frameworks into accessible, actionable guidance for founders and marketing teams.
                </p>
                <p>
                  Our approach combines data-driven insights with creative strategies that we've personally used to achieve exponential growth curves. We don't deal in theory. Every recommendation in our platform has been validated through real market success.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              className="relative h-[260px] sm:h-[340px] md:h-[420px] rounded-2xl border border-border bg-muted shadow-xl overflow-hidden"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={`${import.meta.env.BASE_URL}founders.png`}
                alt="LaunchGuide team working in the office"
                className="h-full w-full object-cover object-center"
                loading="lazy"
                decoding="async"
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Our Values Section */}
      <section className="py-20 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-background to-blue-50/50 dark:from-background dark:to-blue-950/10">
        <div className="container mx-auto relative">
          {/* Background decorative elements */}
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute top-0 left-0 w-full h-full opacity-[0.03] dark:opacity-[0.05]">
              <defs>
                <pattern id="values-grid" width="8" height="8" patternUnits="userSpaceOnUse">
                  <circle cx="4" cy="4" r="1" fill="currentColor" className="text-blue-600" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#values-grid)" />
            </svg>
          </div>
          
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block rounded-full bg-blue-100 dark:bg-blue-900/30 px-3 py-1 text-sm text-blue-700 dark:text-blue-300 mb-4">
              Our Core Values
            </div>
            <h2 className="text-3xl font-bold mb-4">Marketing Principles That Drive Results</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These core principles have guided us in scaling multiple companies to millions in revenue
            </p>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => {
              // Assign an icon to each value
              const icons = [
                <Shield className="h-7 w-7" />,
                <Lightbulb className="h-7 w-7" />,
                <BarChart3 className="h-7 w-7" />,
                <BookOpen className="h-7 w-7" />
              ];
              
              return (
                <motion.div 
                  key={index} 
                  className="bg-white dark:bg-zinc-800/50 rounded-xl p-6 shadow-sm border border-zinc-200 dark:border-zinc-700/50 hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800/50 transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <div className="flex items-start gap-5">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20 flex items-center justify-center text-blue-600 dark:text-blue-400 border border-blue-100 dark:border-blue-800/30">
                        {icons[index]}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2 text-zinc-900 dark:text-white">{value.title}</h3>
                      <p className="text-muted-foreground">{value.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      
      {/* Contact Section */}
      <section className="py-20 px-4 md:px-6 lg:px-8 bg-white dark:bg-zinc-900/50">
        <div className="container mx-auto">
          <motion.div 
            className="max-w-4xl mx-auto text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-block rounded-full bg-blue-100 dark:bg-blue-900/30 px-3 py-1 text-sm text-blue-700 dark:text-blue-300 mb-4">
              Connect With Us
            </div>
            <h2 className="text-3xl font-bold mb-4">Get Marketing Support For Your Business</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-10">
              Want to learn how our marketing professionals can help scale your business? Reach out to discuss how we've helped companies like yours achieve exponential growth.
            </p>
            
            {/* Modern contact options */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              <motion.div 
                className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 flex flex-col items-center hover:shadow-md transition-all"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.1 }}
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                  <Mail className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-1 text-zinc-900 dark:text-white">Email Us</h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-3">For general inquiries</p>
                <a href="mailto:hello@launchguide.io" className="text-blue-600 dark:text-blue-400 font-medium text-sm hover:underline">
                  hello@launchguide.io
                </a>
              </motion.div>
              
              <motion.div 
                className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 flex flex-col items-center hover:shadow-md transition-all"
                whileHover={{ y: -5 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.3 }}
              >
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-800/50 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-4">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-1 text-zinc-900 dark:text-white">Support</h3>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-3">Need technical help?</p>
                <a href="mailto:support@launchguide.io" className="text-blue-600 dark:text-blue-400 font-medium text-sm hover:underline">
                  support@launchguide.io
                </a>
              </motion.div>
            </div>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground hover:shadow-md transition-all" asChild>
                <a href="mailto:hello@launchguide.io" className="flex items-center">
                  <Mail className="mr-2 h-4 w-4" /> Contact Us
                </a>
              </Button>
              <Button variant="outline" className="border-zinc-200 text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900 transition-all" asChild>
                <Link to="/auth/register">
                  Try LaunchGuide for Free <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default About; 